import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSchemaScopedQueryKey } from '@/lib/schemaStorage';
import { fetchWithSchema } from '@/lib/api';
import type { StaticPage } from '@webready/shared/schema';
import { useAppConfiguration } from '@/hooks/useAppConfiguration';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { logger } from '@/lib/logger';

export interface NavigationLink {
  href: string;
  label: string;
}

/**
 * Hook pentru obținerea link-urilor de navigare dinamică
 * Folosește React Query cache pentru a partaja datele între Navbar și Footer
 */
export function useNavigationLinks() {
  const { appConfig } = useAppConfiguration();
  const { isFeatureEnabled } = useSubscription();

  // Fetch main static pages (custom: false) - shared query key cu Navbar
  const { data: mainPages, isLoading: isLoadingMain } = useQuery<StaticPage[]>({
    queryKey: getSchemaScopedQueryKey(['static-pages-main']),
    queryFn: async () => {
      const res = await fetchWithSchema('/api/static-pages/main');
      let text = await res.text();
      try {
        const json = JSON.parse(text);
        if (!res.ok) throw new Error('Failed to fetch main static pages');
        return json;
      } catch (e) {
        logger.error('Error parsing response /api/static-pages/main:', text);
        throw e;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes cache
  });

  // Fetch custom static pages (custom: true) - shared query key cu Navbar
  const { data: customPages, isLoading: isLoadingCustom } = useQuery<StaticPage[]>({
    queryKey: getSchemaScopedQueryKey(['static-pages-nav']),
    queryFn: async () => {
      const res = await fetchWithSchema('/api/static-pages/nav');
      if (!res.ok) throw new Error('Failed to fetch custom static pages');
      return res.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes cache
  });

  // Build navigation links from main pages and custom pages
  // Exact same logic as Navbar component
  const navigationLinks = useMemo(() => {
    // Determine application type
    const appointmentType = appConfig?.appointmentType;
    const isWebsitePresentation = appointmentType === 'website_presentation';
    const isBookingEnabled = isFeatureEnabled('enable_booking') && 
      (appointmentType === 'clinic_hours' || appointmentType === 'lodging_days');
    
    // Build mainNavLinks - same as Navbar
    const sortedMainPages = (mainPages || []).sort((a, b) => {
      const aOrder = a.sort_order ?? 9999;
      const bOrder = b.sort_order ?? 9999;
      return aOrder - bOrder;
    });
    
    const mainNavLinks = sortedMainPages.map(p => ({ href: `/${p.slug}`, label: p.menu_label }));
    
    // Build allLinks - same as Navbar (filters /services if needed)
    // Don't filter if appConfig is not loaded yet
    const allLinks = !appConfig 
      ? mainNavLinks
      : mainNavLinks.filter(link => {
          // Only filter out /services if booking is disabled AND not in presentation mode
          if (!isBookingEnabled && !isWebsitePresentation && (link.href === '/services' || link.href.startsWith('/services'))) {
            return false;
          }
          return true;
        });
    
    const sortedCustomPages = (customPages || []).sort((a, b) => {
      const aOrder = a.sort_order ?? 9999;
      const bOrder = b.sort_order ?? 9999;
      return aOrder - bOrder;
    });
    
    const customNavLinks = sortedCustomPages.map(p => ({
      href: p.is_hub_link ? `/site/${p.slug}` : `/page/${p.slug}`,
      label: p.menu_label
    }));
    
    // Combine allLinks + customNavLinks (same as Navbar's visibleLinks + hiddenLinks combined)
    const combinedLinks = [...allLinks, ...customNavLinks];
    
    logger.debug('[useNavigationLinks] Building links:', {
      mainPagesCount: mainPages?.length || 0,
      customPagesCount: customPages?.length || 0,
      mainNavLinksCount: mainNavLinks.length,
      allLinksCount: allLinks.length,
      customNavLinksCount: customNavLinks.length,
      combinedLinksCount: combinedLinks.length,
      appointmentType,
      isBookingEnabled,
      isWebsitePresentation
    });

    return combinedLinks;
  }, [mainPages, customPages, appConfig, isFeatureEnabled]);

  return {
    links: navigationLinks,
    isLoading: isLoadingMain || isLoadingCustom,
  };
}
